// /app/frontend/src/components/frugal-v2/components/AddItemForm.jsx
// Inline form to add items with dynamic categories (130 lines)

import React, { useState } from 'react';
import { Plus, X } from 'lucide-react';
import { EXPENSE_CATEGORIES, INCOME_CATEGORIES, SAVING_CATEGORIES, translateCategory } from '../constants/categories';

// Get categories based on type
const getCategoriesByType = (type, customCategories) => {
  if (customCategories) return customCategories;
  switch (type) {
    case 'income': return INCOME_CATEGORIES;
    case 'saving': return SAVING_CATEGORIES;
    case 'expense': return EXPENSE_CATEGORIES;
    default: return EXPENSE_CATEGORIES;
  }
};

const AddItemForm = ({
  type, // 'income' | 'saving' | 'expense'
  onAdd,
  onCancel,
  colorClass,
  showCategory = false,
  categories: customCategories,
  t
}) => {
  const categories = getCategoriesByType(type, customCategories);
  const [name, setName] = useState('');
  const [amount, setAmount] = useState('');
  const [day, setDay] = useState('1');
  const [category, setCategory] = useState(categories[0] || 'Otros');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!name.trim() || !amount) return;

    setIsSubmitting(true);
    try {
      await onAdd({
        name: name.trim(),
        amount: parseFloat(amount),
        day: parseInt(day),
        category: showCategory ? category : undefined,
        recurring: true
      });
      setName('');
      setAmount('');
      setDay('1');
      setCategory(categories[0] || 'Otros');
      onCancel();
    } catch (error) {
      console.error('Error adding item:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="p-3 bg-white/5 rounded-lg border border-white/10">
      <div className="space-y-2">
        {/* Row 1: Day + Name */}
        <div className="grid grid-cols-12 gap-2">
          <div className="col-span-3">
            <input
              type="number"
              min="1"
              max="31"
              value={day}
              onChange={(e) => setDay(e.target.value)}
              placeholder={t(`frugal.form.day`)}
              className="w-full px-2 py-2 bg-white/10 border border-white/20 text-white text-sm rounded-md focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
            />
          </div>
          <div className="col-span-9">
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={t(`frugal.form.description`)}
              className="w-full px-2 py-2 bg-white/10 border border-white/20 text-white text-sm rounded-md focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
              autoFocus
            />
          </div>
        </div>

        {/* Row 2: Category (scrollable) + Amount */}
        <div className="grid grid-cols-12 gap-2">
          {showCategory && (
            <div className="col-span-7">
              <select
                value={category}
                onChange={(e) => setCategory(e.target.value)}
                className="w-full px-2 py-2 bg-white/10 border border-white/20 text-white text-sm rounded-md focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 max-h-40 overflow-y-auto"
                style={{ maxHeight: '200px' }}
              >
                {categories.map(cat => (
                  <option key={cat} value={cat} className="bg-slate-800 py-1">
                    {translateCategory(t, cat)}
                  </option>
                ))}
              </select>
            </div>
          )}
          <div className={showCategory ? 'col-span-5' : 'col-span-12'}>
            <input
              type="number"
              step="0.01"
              min="0"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              placeholder={t(`frugal.form.amount`)}
              className="w-full px-2 py-2 bg-white/10 border border-white/20 text-white text-sm rounded-md focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
            />
          </div>
        </div>

        {/* Row 3: Actions */}
        <div className="flex gap-2 pt-1">
          <button
            type="submit"
            disabled={isSubmitting || !name.trim() || !amount}
            className={`flex-1 flex items-center justify-center gap-2 py-2 rounded-md text-white transition-colors ${colorClass} disabled:opacity-50`}
          >
            <Plus className="w-4 h-4" />
            {t('frugal.actions.add')}
          </button>
          <button
            type="button"
            onClick={onCancel}
            className="px-4 py-2 rounded-md bg-white/10 hover:bg-white/20 text-slate-400 transition-colors"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>
    </form>
  );
};

export default AddItemForm;
